import React, { lazy, useEffect } from "react";
import { Flip, ToastContainer, toast } from "react-toastify";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import 'react-medium-image-zoom/dist/styles.css'

import { Layout, ProtectedRoutes } from "./Components";
import {
  NotFound, SignIn
} from "./Pages";
import ConfirmationModal from "./Components/Modals/ConfirmationModal";
import Splash from "./Pages/Splash/Splash";

import { useSelector } from "react-redux";
import { setExpiryTime, setToastMessage } from "./reducers/contextSlice";
import { useDispatch } from "react-redux";
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Route-level code splitting: each page is loaded on demand as its own chunk,
// keeping the initial bundle small. The Suspense fallback lives in Layout so
// the sidebar/header stay mounted while a page chunk loads.
// DASHBOARD
const HomeActivity = lazy(() => import("./Pages/HomeActivity/HomeActivity"));
const Redirectional = lazy(() => import("./Pages/Home/Redirectional/Redirectional"));
// DIY
const Background = lazy(() => import("./Pages/DYIs/Background/Background"));
const BackgroundCategories = lazy(() => import("./Pages/DYIs/BackgroundCategories/BackgroundCategories"));
const Keys = lazy(() => import("./Pages/DYIs/Keys/Keys"));
const KeysCategories = lazy(() => import("./Pages/DYIs/KeysCategories/KeysCategories"));
const FontList = lazy(() => import("./Pages/DYIs/FontList/FontList"));
const Effects = lazy(() => import("./Pages/DYIs/Effects/Effects"));
const Sounds = lazy(() => import("./Pages/DYIs/Sounds/Sounds"));
const Lenses = lazy(() => import("./Pages/DYIs/Lense/Lense"));
const Swipes = lazy(() => import("./Pages/DYIs/Swipe/Swipes"));
const Auto = lazy(() => import("./Pages/DYIs/Auto/Auto"));
// DIY json
const KeysJson = lazy(() => import("./Pages/DYIsJson/KeysJson"));
const LenseJson = lazy(() => import("./Pages/DYIsJson/LenseJson"));
const AutoJson = lazy(() => import("./Pages/DYIsJson/AutoJson"));
// Theme
const ThemeStore = lazy(() => import("./Pages/Theme/ThemeStore/ThemeStore"));
const ThemeTag = lazy(() => import("./Pages/Theme/ThemeTag/ThemeTag"));
const ThemeHashtag = lazy(() => import("./Pages/Theme/ThemeHashtag/ThemeHashtag"));
const ThemesColor = lazy(() => import("./Pages/Theme/ThemesColor/ThemesColor"));
const ThemeComment = lazy(() => import("./Pages/Theme/ThemesComment/ThemeComment"));
const ThemesCategories = lazy(() => import("./Pages/Theme/ThemesCategories/ThemesCategories"));
const ReportReason = lazy(() => import("./Pages/Theme/ReportReason/ReportReason"));
const ReportedComment = lazy(() => import("./Pages/Theme/ReportReason/ReportedComment"));
const ZipValidation = lazy(() => import("./Pages/ZipValidation/ZipValidation"));
const RcAiChatConfig = lazy(() => import("./Pages/RcAiChatConfig/RcAiChatConfig"));
// Stickers
const StickerPack = lazy(() => import("./Pages/StickerPack/StickerPack"));
const StickerStore = lazy(() => import("./Pages/WhatsAppSticker/StickerStore"));
const StickerTag = lazy(() => import("./Pages/WhatsAppSticker/StickerTag"));
// Stores / settings
const BannerStore = lazy(() => import("./Pages/BannerStore/BannerStore"));
const SubscriptionIcon = lazy(() => import("./Pages/SubscriptionIcon/SubscriptionIcon"));
const Settings = lazy(() => import("./Pages/Settings/Settings"));
const Version = lazy(() => import('./Pages/Version/Version'));
// History
const History = lazy(() => import("./Pages/History/History"));

const socket = require('socket.io-client').connect(process.env.REACT_APP_SUPER_SOCKET_URL, { transports: ['polling'] })

dayjs.extend(utc)
dayjs.extend(timezone)

const App = () => {
  const { toastMessage } = useSelector(state => state.context)
  const dispatch = useDispatch();

  useEffect(() => {
    socket.on(process.env.REACT_APP_PROJECT_ID, async (data) => {
      localStorage.setItem('token', data)
      return () => socket.off(process.env.REACT_APP_PROJECT_ID);
    });
  }, [socket])

  useEffect(() => {
    const handleActivity = (e) => {
      if (!e.target.classList.contains('bypass-timeout')) {
        dispatch(setExpiryTime());
      }
    };

    document.addEventListener('click', handleActivity);
    document.addEventListener('keydown', handleActivity);

    return () => {
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keydown', handleActivity);
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      dispatch(setExpiryTime());
    }
  }, []);

  useEffect(() => {
    if (toastMessage?.message !== '') {
      toast[toastMessage?.type](toastMessage?.message)
    }
    dispatch(setToastMessage({ type: 'success', message: '' }));
  }, [toastMessage?.message])

  return (
    <>
      <ToastContainer position="bottom-center" transition={Flip} hideProgressBar={true} pauseOnFocusLoss={false} autoClose={400} style={{ zIndex: "9999999" }} theme="dark" />
      <Router>
        <Routes>
          <Route exact path="/" element={<ProtectedRoutes />}>
            <Route index element={<Navigate to={'/homescreen'} />} />
            <Route element={<Layout />}>

              {/* DASHBOARD */}
              <Route exact path="/homescreen" element={<HomeActivity />} />

              <Route exact path="/redirectional" element={<Redirectional />} />
              {/* Diy */}
              <Route exact path="/backgrounds" element={<Background />} />
              <Route exact path="/backgroundcategories" element={<BackgroundCategories />} />
              <Route exact path="/keys" element={<Keys />} />
              <Route exact path="/keyscategories" element={<KeysCategories />} />
              <Route exact path="/fontlists" element={<FontList />} />
              <Route exact path="/effects" element={<Effects />} />
              <Route exact path="/sounds" element={<Sounds />} />
              <Route exact path="/lenses" element={<Lenses />} />
              <Route exact path="/swipes" element={<Swipes />} />
              <Route exact path="/auto" element={<Auto />} />
              {/* json */}
              <Route exact path="/keysjson" element={<KeysJson />} />
              <Route exact path="/lensejson" element={<LenseJson />} />
              <Route exact path="/autojson" element={<AutoJson />} />

              {/* theme */}
              <Route exact path="/themestore" element={<ThemeStore />} />
              <Route exact path="/themetags" element={<ThemeTag />} />
              <Route exact path="/themehashtags" element={<ThemeHashtag />} />
              <Route exact path="/themecolor" element={<ThemesColor />} />
              <Route exact path="/themecomment" element={<ThemeComment />} />
              <Route exact path="/themecategories" element={<ThemesCategories />} />
              <Route exact path="/reportreason" element={<ReportReason />} />
              <Route exact path="/reportedcomments" element={<ReportedComment />} />
              <Route exact path="/zip-validation" element={<ZipValidation />} />
              <Route exact path="/rcconfig" element={<RcAiChatConfig />} />

              {/* Sticker generation  */}
              <Route exact path="/stickerpack" element={<StickerPack />} />
              <Route exact path="/stickerstore" element={<StickerStore />} />
              <Route exact path="/stickertag" element={<StickerTag />} />

              {/* Stores */}
              <Route exact path="/bannerstore" element={<BannerStore />} />
              <Route exact path="/subscriptionicon" element={<SubscriptionIcon />} />
              <Route exact path="/settings" element={<Settings />} />
              <Route exact path="/version" element={<Version />} />


              {/* History */}
              <Route exact path="/history" element={<History />} />
            </Route>
          </Route>

          <Route
            exact
            path="/splash/:loginType/:userName/:token"
            element={<Splash />}
          />

          <Route
            exact
            path="/signin"
            element={localStorage.getItem("token") ? (<Navigate to={"/"} />) : (<SignIn />)}
          />
          <Route exact path="/notfound" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/notfound" />} />
        </Routes>
        <ConfirmationModal />
      </Router>
    </>
  );
};

export default App;
